import Authenticated from "@/components/auth/Authenticated";
import { SCLink } from "@/components/common/SCLink";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { useSleepCheckupInfoAPI } from "@/components/examinee/sleep_checkup/SleepCheckupAPI";
import { SleepCheckupInfo } from "@/components/examinee/sleep_checkup/Types";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import constants from "@/utils/constants";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, LinkProps, useParams } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  sleepCheckupUuid: string;
};

export function DeviceIdScanIntroduction() {
  const params = useParams<StateType>();
  const [info, setInfo] = useState<SleepCheckupInfo>();
  const api = useSleepCheckupInfoAPI();
  useEffect(() => {
    (async () => {
      if (params.sleepCheckupUuid == null) return;
      const res = await api.get(params.sleepCheckupUuid);
      setInfo(res.data);
    })();
  }, [api, params.sleepCheckupUuid]);

  if (params.sleepCheckupUuid == null) {
    return (
      <GeneralError
        to="/examinee/signup"
        buttonText="アカウント登録をやり直す"
      />
    );
  }

  const state = { sleepCheckupUuid: params.sleepCheckupUuid };

  return (
    <Authenticated redirectTo={constants.SIGNIN_URL_EXAMINEE}>
      <ScrollToTop />
      <LinearProgress
        variant="determinate"
        value={(4 / 6) * 100}
        sx={{
          borderRadius: 1,
          mx: 4,
          my: 6,
          height: "6px",
        }}
      />
      <Stack sx={{ mb: 16 }}>
        <Stack sx={{ py: 6 }}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "170%",
              letterSpacing: "0.15px",
            }}
          >
            デバイスIDの登録
          </Typography>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "190%",
              letterSpacing: "0.1px",
              color: "text.secondary",
            }}
          >
            （腕時計型デバイス）
          </Typography>
        </Stack>
        <Stack spacing={4} sx={{ mx: 4 }}>
          {info != null && (
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Noto Sans JP",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "190%",
                letterSpacing: "0.1px",
                textAlign: "justify",
                color: "text.secondary",
              }}
            >
              測定に利用するデバイスの登録を行います。
              {info.recommendation_option && (
                <>
                  まずは「腕時計型デバイス」のID登録を行い、続けて「リング型デバイス」のID登録を行います。
                  <br />
                </>
              )}
              本ページ下部の[QRコードを読み取る]ボタンを押して、お使いのスマートフォンでデバイスの腕側に表示されているQRコードを読み取ってください。
            </Typography>
          )}
          <DeviceIntroductionImage src="/img/image_device_id_scan_introduction_01.png" />
          <Stack spacing={2}>
            <DeviceIntroductionImage src="/img/image_device_id_scan_introduction_02.png" />
            <Typography
              variant="caption"
              sx={{
                fontFamily: "Noto Sans JP",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "175%",
                letterSpacing: "0.4px",
                color: "text.secondary",
              }}
            >
              ※デバイスの色についてはイラストと異なる場合があります。
            </Typography>
          </Stack>
          <Stack spacing={4} sx={{ py: 6 }}>
            <QRCodeScanButton
              title="QRコードを読み取る"
              subTitle="※カメラが起動します"
              to="/examinee/device_id_scan"
              state={state}
            />
            <SCLink
              variant="body2"
              to="/examinee/device_id_input"
              state={state}
              sx={{ color: "text.secondary" }}
            >
              うまく読み取れない場合
            </SCLink>
          </Stack>
        </Stack>
      </Stack>
    </Authenticated>
  );
}

type DeviceIntroductionImageProps = {
  src: string;
};
function DeviceIntroductionImage({ src }: DeviceIntroductionImageProps) {
  return (
    <img
      src={src}
      alt="デバイスIDの登録"
      loading="lazy"
      style={{ width: "100%", height: "auto" }}
    />
  );
}

type QRCodeScanButtonProps = {
  title: string;
  subTitle: string;
  to: LinkProps["to"];
  state: LinkProps["state"];
};
function QRCodeScanButton({
  title,
  subTitle,
  to,
  state,
}: QRCodeScanButtonProps) {
  return (
    <Button
      component={Link}
      to={to}
      state={state}
      variant="contained"
      size="large"
    >
      <Stack alignItems="center">
        <Typography variant="button" color="white" fontSize="15px">
          {title}
        </Typography>
        <Typography variant="caption" color="white">
          {subTitle}
        </Typography>
      </Stack>
    </Button>
  );
}
